/**
 * Filter state for the Procedure Templates table (z8nrz7czh6).
 *
 * Every filter here is applied SERVER-side, and that is deliberate rather than
 * incidental: the table is server-paged, so narrowing the returned page would
 * hide matches on every other page and leave the `n–m of N` footer counting a
 * set the user can never reach. The same reasoning already governs the type
 * filter (see the comment above `listPaged` in `src/api/procedureTemplatesApi.ts`);
 * search and status join it rather than being filtered in the component.
 *
 * Pure — no React, no network — because `vitest.config.ts` is
 * `environment: node` with `include: ['src/**\/*.test.ts']`, so anything
 * expressed inside the `.tsx` page could never be tested.
 */

import { FORM_CATEGORY_LABELS, PROCEDURE_TYPE_LABELS, type FormCategory, type ProcedureType } from '@/types/cgt'

/** 'all' is the no-filter sentinel — shadcn's Select cannot hold an empty value. */
export type TypeFilter = ProcedureType | 'all'
export type StatusFilter = 'all' | 'active' | 'inactive'
/** A template matches when at least one of its forms has this Form Type. */
export type FormTypeFilter = FormCategory | 'all'

export interface TemplateFilters {
  search: string
  type: TypeFilter
  status: StatusFilter
  formType: FormTypeFilter
}

export const EMPTY_FILTERS: TemplateFilters = { search: '', type: 'all', status: 'all', formType: 'all' }

/**
 * What the table opens with: active templates only. Status is still a filter
 * like any other — pick Any status or Inactive to see the rest. Clear filters
 * goes to EMPTY_FILTERS, so it really does show everything.
 */
export const DEFAULT_FILTERS: TemplateFilters = { ...EMPTY_FILTERS, status: 'active' }

/** True when nothing is narrowing the list. Drives which empty state is shown. */
export function isUnfiltered(filters: TemplateFilters): boolean {
  return (
    filters.search.trim() === '' &&
    filters.type === 'all' &&
    filters.status === 'all' &&
    filters.formType === 'all'
  )
}

/**
 * The query arguments for `procedureTemplatesApi.listPaged`.
 *
 * Absent keys rather than empty ones: `listPaged` spreads what it is given
 * straight into `URLSearchParams`, and `{ search: '' }` would serialise as
 * `search=`, which the backend reads as a real (empty) term rather than as no
 * term at all.
 */
export function toListParams(
  filters: TemplateFilters,
): { type?: ProcedureType; search?: string; active?: boolean; formCategory?: FormCategory } {
  const search = filters.search.trim()
  return {
    ...(filters.type !== 'all' ? { type: filters.type } : {}),
    ...(filters.formType !== 'all' ? { formCategory: filters.formType } : {}),
    ...(search ? { search } : {}),
    ...(filters.status !== 'all' ? { active: filters.status === 'active' } : {}),
  }
}

/**
 * What the empty state should say.
 *
 * `null` means "nothing exists yet" — the first-run case, which is the only one
 * where offering "create one" is the right next step. A string means the list is
 * empty *because of* the filters, and names them, so the user can tell a typo
 * from a genuinely empty tenant.
 */
export function emptyStateMessage(filters: TemplateFilters): string | null {
  if (isUnfiltered(filters)) return null

  const parts: string[] = []
  const search = filters.search.trim()
  if (search) parts.push(`matching “${search}”`)
  if (filters.type !== 'all') parts.push(`of type ${PROCEDURE_TYPE_LABELS[filters.type]}`)
  if (filters.formType !== 'all') parts.push(`that include ${FORM_CATEGORY_LABELS[filters.formType]} forms`)
  if (filters.status !== 'all') parts.push(filters.status === 'active' ? 'that are active' : 'that are inactive')

  return `No templates ${joinReadable(parts)}.`
}

/** "a", "a and b", "a, b and c" — an Oxford-comma-free list for prose. */
function joinReadable(parts: string[]): string {
  if (parts.length === 0) return ''
  if (parts.length === 1) return parts[0]
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
}

/**
 * Filters that will show a freshly created template.
 *
 * Used after a copy. The bug this exists for is NOT a stale list: the page
 * already refetches, but it refetches the page the user is on, and the list is
 * paged over families sorted by name — so a copy called "Copy of …" lands on
 * some other page and is silently absent. A browser reload only appears to fix
 * it because it resets the page to 0.
 *
 * Searching for the new name instead of merely resetting the page is what makes
 * this deterministic: page 1 of an unfiltered list is still the wrong page once
 * a tenant has more templates than fit on it.
 */
export function filtersRevealing(name: string): TemplateFilters {
  return { ...EMPTY_FILTERS, search: name.trim() }
}
